import { create } from 'zustand';

// Sentinel for the Recipes page category chip row: recipes that resolve to no
// category at all (no cost price link, or a link with no category).
export const RECIPES_UNASSIGNED_CATEGORY = '__unassigned__';

export type CategoryTypeFilter = 'all' | 'food' | 'non-food';
export type OverallTypeFilter = 'all' | 'food' | 'drinks' | 'non-food' | 'take-away' | 'other' | 'dish' | 'ingredient';
export type PriceAlertChangeType = 'all' | 'increases' | 'decreases';

interface FiltersState {
  // Overall type filter - shared across dashboard, order history and revenue history (top section)
  overallTypeFilter: OverallTypeFilter;
  setOverallTypeFilter: (filter: OverallTypeFilter) => void;

  // Section filters - DEPRECATED for main pages, will be moved to filter modal
  // Still used by OrderHistoryProductsPage, OrderHistoryOrdersPage, RevenueHistoryRevenuePage, etc.
  sectionFilters: string[];
  setSectionFilters: (filters: string[]) => void;

  // Restaurant filters - for "All Restaurants" view (multi-select). Empty = all restaurants.
  restaurantFilters: string[];
  setRestaurantFilters: (filters: string[]) => void;

  // Platform filters - for order history
  platformFilters: string[];
  setPlatformFilters: (filters: string[]) => void;

  // Category type filter - for order history (all/food/non-food)
  categoryTypeFilter: CategoryTypeFilter;
  setCategoryTypeFilter: (filter: CategoryTypeFilter) => void;

  // Category filters - for order history (specific product categories)
  categoryFilters: string[];
  setCategoryFilters: (filters: string[]) => void;

  // Subcategory filters - for order history (specific product subcategories)
  subCategoryFilters: string[];
  setSubCategoryFilters: (filters: string[]) => void;

  // Revenue category filters - for revenue history (different from order categories)
  revenueCategoryFilters: string[];
  setRevenueCategoryFilters: (filters: string[]) => void;

  // Revenue source filters - for revenue history. Holds SOURCE NAMES
  // (restaurant_revenue_sources.restaurant_name), not source ids: one venue can own
  // several source rows (bar POP has a FOOD and an OTHER row). Empty = all sources.
  revenueSourceFilters: string[];
  setRevenueSourceFilters: (filters: string[]) => void;

  // Menu category filters - for revenue history items
  menuCategoryFilters: string[];
  setMenuCategoryFilters: (filters: string[]) => void;

  // Price alert platform filters - for price alerts
  priceAlertPlatformFilters: string[];
  setPriceAlertPlatformFilters: (filters: string[]) => void;

  // Price alert category filters - for price alerts
  priceAlertCategoryFilters: string[];
  setPriceAlertCategoryFilters: (filters: string[]) => void;

  // Price alert change type filter - for price alerts (all/increases/decreases)
  priceAlertChangeType: PriceAlertChangeType;
  setPriceAlertChangeType: (type: PriceAlertChangeType) => void;

  // Staff restaurant filters - for staff management page
  staffRestaurantFilters: string[];
  setStaffRestaurantFilters: (filters: string[]) => void;

  // Order search query - for order history orders page
  orderSearchQuery: string;
  setOrderSearchQuery: (query: string) => void;

  // Matrix filters - for cost price page (menu engineering matrix)
  matrixFilters: string[];
  setMatrixFilters: (filters: string[]) => void;

  // Chart category filters - for menu engineering chart (cost price categories)
  chartCategoryFilters: string[];
  setChartCategoryFilters: (filters: string[]) => void;

  // Chart menu filters - for menu engineering chart (cost price menus)
  chartMenuFilters: string[];
  setChartMenuFilters: (filters: string[]) => void;

  // Cost price menu filter - for the cost price page menu dropdown.
  // Reserved sentinel ids: '__all__' and '__active_dishes__'. The array is
  // either ['__all__'], ['__active_dishes__'], or one-or-more real menu ids.
  costPriceMenuFilters: string[];
  setCostPriceMenuFilters: (filters: string[]) => void;

  // Recipes page menu filter - same multi-select semantics as cost price
  // (sentinels '__all__' and '__active_dishes__', otherwise real menu ids).
  recipesMenuFilters: string[];
  setRecipesMenuFilters: (filters: string[]) => void;

  // Recipes page category filter - SINGLE-select, matching the cost price chip
  // row and both mobile apps. Reserved sentinels: 'all' and RECIPES_UNASSIGNED_CATEGORY
  // (recipes that resolve to no category at all), otherwise a real category id.
  recipesCategoryFilter: string;
  setRecipesCategoryFilter: (categoryId: string) => void;

  // "Initialized for restaurant X" gates for the three menu filters above.
  // Live in the store (not in component refs) so they survive page
  // navigation — otherwise the page's init effect would re-run on every
  // remount and overwrite the user's manual selection. Reset to null on a
  // hard refresh (in-memory store) so defaults are re-applied per session.
  costPriceMenuFiltersInitForRestaurantId: string | null;
  setCostPriceMenuFiltersInitForRestaurantId: (id: string | null) => void;
  recipesMenuFiltersInitForRestaurantId: string | null;
  setRecipesMenuFiltersInitForRestaurantId: (id: string | null) => void;
  chartMenuFiltersInitForRestaurantId: string | null;
  setChartMenuFiltersInitForRestaurantId: (id: string | null) => void;

  // Sort state - for cost price page
  costPriceSortColumn: string | null;
  costPriceSortDirection: 'asc' | 'desc' | null;
  setCostPriceSort: (column: string | null, direction: 'asc' | 'desc' | null) => void;

  // Sort state - for order history products page
  orderHistoryProductsSortColumn: string | null;
  orderHistoryProductsSortDirection: 'asc' | 'desc' | null;
  setOrderHistoryProductsSort: (column: string | null, direction: 'asc' | 'desc' | null) => void;

  // Reset all filters
  resetFilters: () => void;

  // Reset only restaurant filters
  resetRestaurantFilters: () => void;

  // Reset only order filters
  resetOrderFilters: () => void;

  // Reset only revenue filters
  resetRevenueFilters: () => void;

  // Reset only price alert filters
  resetPriceAlertFilters: () => void;

  // Reset only staff restaurant filters
  resetStaffRestaurantFilters: () => void;
}

export const useFiltersStore = create<FiltersState>((set) => ({
  overallTypeFilter: 'all',
  setOverallTypeFilter: (filter) => set({ overallTypeFilter: filter }),

  sectionFilters: [],
  setSectionFilters: (filters) => set({ sectionFilters: filters }),

  restaurantFilters: [],
  setRestaurantFilters: (filters) => set({ restaurantFilters: filters }),

  platformFilters: [],
  setPlatformFilters: (filters) => set({ platformFilters: filters }),

  categoryTypeFilter: 'all',
  setCategoryTypeFilter: (filter) => set({ categoryTypeFilter: filter }),

  categoryFilters: [],
  setCategoryFilters: (filters) => set({ categoryFilters: filters }),

  subCategoryFilters: [],
  setSubCategoryFilters: (filters) => set({ subCategoryFilters: filters }),

  revenueCategoryFilters: [],
  setRevenueCategoryFilters: (filters) => set({ revenueCategoryFilters: filters }),

  revenueSourceFilters: [],
  setRevenueSourceFilters: (filters) => set({ revenueSourceFilters: filters }),

  menuCategoryFilters: [],
  setMenuCategoryFilters: (filters) => set({ menuCategoryFilters: filters }),

  priceAlertPlatformFilters: [],
  setPriceAlertPlatformFilters: (filters) => set({ priceAlertPlatformFilters: filters }),

  priceAlertCategoryFilters: [],
  setPriceAlertCategoryFilters: (filters) => set({ priceAlertCategoryFilters: filters }),

  priceAlertChangeType: 'all',
  setPriceAlertChangeType: (type) => set({ priceAlertChangeType: type }),

  staffRestaurantFilters: [],
  setStaffRestaurantFilters: (filters) => set({ staffRestaurantFilters: filters }),

  orderSearchQuery: '',
  setOrderSearchQuery: (query) => set({ orderSearchQuery: query }),

  matrixFilters: [],
  setMatrixFilters: (filters) => set({ matrixFilters: filters }),

  chartCategoryFilters: [],
  setChartCategoryFilters: (filters) => set({ chartCategoryFilters: filters }),

  chartMenuFilters: [],
  setChartMenuFilters: (filters) => set({ chartMenuFilters: filters }),

  costPriceMenuFilters: ['__all__'],
  setCostPriceMenuFilters: (filters) => set({ costPriceMenuFilters: filters }),

  recipesMenuFilters: ['__all__'],
  setRecipesMenuFilters: (filters) => set({ recipesMenuFilters: filters }),

  recipesCategoryFilter: 'all',
  setRecipesCategoryFilter: (categoryId) => set({ recipesCategoryFilter: categoryId }),

  costPriceMenuFiltersInitForRestaurantId: null,
  setCostPriceMenuFiltersInitForRestaurantId: (id) => set({ costPriceMenuFiltersInitForRestaurantId: id }),
  recipesMenuFiltersInitForRestaurantId: null,
  setRecipesMenuFiltersInitForRestaurantId: (id) => set({ recipesMenuFiltersInitForRestaurantId: id }),
  chartMenuFiltersInitForRestaurantId: null,
  setChartMenuFiltersInitForRestaurantId: (id) => set({ chartMenuFiltersInitForRestaurantId: id }),

  costPriceSortColumn: 'costPercentage',
  costPriceSortDirection: 'asc',
  setCostPriceSort: (column, direction) => set({
    costPriceSortColumn: column,
    costPriceSortDirection: direction
  }),

  orderHistoryProductsSortColumn: 'spend',
  orderHistoryProductsSortDirection: 'desc',
  setOrderHistoryProductsSort: (column, direction) => set({
    orderHistoryProductsSortColumn: column,
    orderHistoryProductsSortDirection: direction
  }),

  resetFilters: () => set({
    overallTypeFilter: 'all',
    sectionFilters: [],
    restaurantFilters: [],
    platformFilters: [],
    categoryTypeFilter: 'all',
    categoryFilters: [],
    subCategoryFilters: [],
    revenueCategoryFilters: [],
    revenueSourceFilters: [],
    menuCategoryFilters: [],
    priceAlertPlatformFilters: [],
    priceAlertCategoryFilters: [],
    priceAlertChangeType: 'all',
    staffRestaurantFilters: [],
    orderSearchQuery: '',
    matrixFilters: [],
    chartCategoryFilters: [],
    chartMenuFilters: [],
    costPriceMenuFilters: ['__all__'],
    recipesMenuFilters: ['__all__'],
    recipesCategoryFilter: 'all',
    costPriceMenuFiltersInitForRestaurantId: null,
    recipesMenuFiltersInitForRestaurantId: null,
    chartMenuFiltersInitForRestaurantId: null,
    costPriceSortColumn: 'costPercentage',
    costPriceSortDirection: 'asc',
    orderHistoryProductsSortColumn: 'spend',
    orderHistoryProductsSortDirection: 'desc',
  }),

  resetRestaurantFilters: () => set({
    restaurantFilters: [],
  }),

  resetOrderFilters: () => set({
    restaurantFilters: [],
    platformFilters: [],
    sectionFilters: [],
    categoryFilters: [],
    subCategoryFilters: [],
    orderSearchQuery: '',
  }),

  resetRevenueFilters: () => set({
    revenueCategoryFilters: [],
    revenueSourceFilters: [],
    menuCategoryFilters: [],
  }),

  resetPriceAlertFilters: () => set({
    priceAlertPlatformFilters: [],
    priceAlertCategoryFilters: [],
    priceAlertChangeType: 'all',
    // Note: sectionFilters are shared and not reset here
  }),

  resetStaffRestaurantFilters: () => set({
    staffRestaurantFilters: [],
  }),
}));
